import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type {
    Blocks,
    ContentBlock,
    MediaSlots,
    SiteFrame,
    Testimonial
} from '@/types/content'

import { Container } from '@/components/generals/container'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { ResultsSection } from '@/components/sections/results-section'
import { cms, hasValue } from '@/lib/cms'
import { reveal } from '@/lib/reveal'
import { REVALIDATE_SECONDS, getPageContent, getTestimonials } from '@/lib/content'

interface ResultsPageProps {
    frame: SiteFrame
    blocks: Blocks
    media: MediaSlots
    testimonials: Array<Testimonial>
}

interface ResultsNoteProps {
    block?: ContentBlock
}

// Honest framing of results: individual, not a guarantee.
const ResultsNote: FC<ResultsNoteProps> = ({ block }): ReactNode => {
    if (!block || (!hasValue(block.ueberschrift) && !hasValue(block.text))) return null

    return (
        <section aria-label='Hinweis zu Ergebnissen' className='relative bg-bone py-20 sm:py-28'>
            <Container className='grid gap-6 lg:grid-cols-12 lg:gap-10'>
                {hasValue(block.ueberschrift) && (
                    <p
                        data-mdlfy-cms={cms('inhalte', block.id, 'ueberschrift')}
                        className='type-display text-display-md lg:col-span-5'
                        {...reveal()}
                    >
                        {block.ueberschrift}
                    </p>
                )}

                {hasValue(block.text) && (
                    <p
                        data-mdlfy-cms={cms('inhalte', block.id, 'text')}
                        className='type-lead max-w-2xl text-smoke lg:col-span-6 lg:col-start-7'
                        {...reveal(100)}
                    >
                        {block.text}
                    </p>
                )}
            </Container>
        </section>
    )
}

const ResultsPage: FC<ResultsPageProps> = ({ frame, blocks, media, testimonials }): ReactNode => {
    const seo = blocks['ergebnisse.seo']

    return (
        <Layout frame={frame} overlayHeader>
            <Seo
                title={seo?.ueberschrift ?? 'Ergebnisse'}
                description={seo?.text ?? undefined}
                siteUrl={frame.settings.webseite}
                path='/ergebnisse'
            />

            <ResultsSection
                as='h1'
                block={blocks['ergebnisse.hero']}
                testimonials={testimonials}
                video={media.TESTIMONIAL_VIDEO_01}
                result={media.CUSTOMER_RESULT_01}
                showLink={false}
                showAll
                className='pt-[calc(var(--header-height)+3.5rem)] sm:pt-[calc(var(--header-height)+5rem)] lg:pt-[calc(var(--header-height)+6rem)]'
            />

            <ResultsNote block={blocks['ergebnisse.hinweis']} />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ResultsPageProps> = async () => {
    const [content, testimonials] = await Promise.all([
        getPageContent({
            blocks: ['ergebnisse.seo', 'ergebnisse.hero', 'ergebnisse.hinweis'],
            media: ['TESTIMONIAL_VIDEO_01', 'CUSTOMER_RESULT_01']
        }),
        getTestimonials()
    ])

    return {
        props: {
            frame: content.frame,
            blocks: content.blocks,
            media: content.media,
            testimonials
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default ResultsPage
